import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  Container,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useSearchParams, useLocation } from 'react-router-dom';
import { regions, authCodes } from '../services/api';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import mainImage from "./../images/main.png";
import TowerImage from "./../images/TowerSpeaker.png";
import { Helmet } from 'react-helmet-async';


const ProductVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [regionCode, setRegionCode] = useState('');
  const [authenticityCode, setAuthenticityCode] = useState('');
  const [region, setRegion] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [qrCodeScanned, setQrCodeScanned] = useState(false);
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [subscribe, setSubscribe] = useState(false);

  // Force title update on location change
  useEffect(() => {
    document.title = 'Check Authenticity – Verify Genuine India Tech Speakers';
  }, [location.pathname]);

  // Auto-dismiss success/error messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    const regionParam = searchParams.get('region');
    console.log('Region param:', regionParam);
    console.log('User agent:', navigator.userAgent);
    
    if (regionParam) {
      setRegionCode(regionParam);
      
      // Check if this is a mobile device (QR code scanned with mobile camera)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log('Is mobile:', isMobile);
      
      // Also check if the page was loaded with a region parameter (likely from QR scan)
      const hasRegionParam = window.location.search.includes('region=');
      console.log('Has region param:', hasRegionParam);
      
      if (isMobile || hasRegionParam) {
        // On mobile or if region param exists, assume QR was scanned
        console.log('Auto-verifying region for mobile or QR scan');
        setQrCodeScanned(true);
        setError(''); // Clear any previous errors
        
        // Auto-verify the region with a small delay to ensure state is set
        setTimeout(() => {
          console.log('Calling verifyRegion with:', regionParam);
          verifyRegion(regionParam);
        }, 100);
      } else {
        // On desktop, require QR scanner
        console.log('Desktop detected - requiring QR scan');
        setError('Please scan the QR code on your product to verify the region');
      }
    }
  }, [searchParams]);

  const verifyRegion = async (code) => {
    console.log('verifyRegion called with:', code);
    try {
      setLoading(true);
      console.log('Calling regions.getByCode with:', code);
      const response = await regions.getByCode(code);
      console.log('Region response:', response);
      setRegion(response.data);
      setSuccess(`Region verified: ${response.data.name}`);
      setQrCodeScanned(true);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Region verification error:', err);
      setError('Invalid region code');
    } finally {
      setLoading(false);
    }
  };

  const handleQRCodeScan = async (qrData) => {
    // This will be called from the CustomerLayout QR scanner
    try {
      setLoading(true);
      setError('');
      
      // Validate QR code with server
      const response = await regions.validateQR(qrData);
      
      if (response.data.valid) {
        // QR code is valid, now verify the region
        await verifyRegion(response.data.region);
      } else {
        setError('Invalid QR code. Please scan the QR code from your product.');
      }
    } catch (error) {
      console.error('QR validation error:', error);
      setError('Failed to validate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Expose the QR scan handler to parent component
  useEffect(() => {
    window.handleQRCodeScan = handleQRCodeScan;
    return () => {
      delete window.handleQRCodeScan;
    };
  }, [regionCode]);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          });
        },
        (error) => {
          console.warn('Location access denied or failed:', error);
          // Return null if location access is denied
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  const handleVerifyProduct = async (e) => {
    e.preventDefault();
    
    // if (!qrCodeScanned) {
    //   setError('Please scan the QR code first to verify the region');
    //   return;
    // }
    
    if (!authenticityCode) {
      setError('Please enter the authenticity code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Get current location
      let locationData = null;
      try {
        locationData = await getCurrentLocation();
        console.log('Location data:', locationData);
      } catch (error) {
        console.warn('Could not get location:', error);
      }
      
      // Use the new dedicated verification API
      const response = await authCodes.verifyProductCode({
        authenticityCode,
        location: locationData,
        userAgent: navigator.userAgent,
        ipAddress: null // Will be captured on server side
      });
      
      console.log('Verification API Response:', response);
      console.log('Response data:', response.data);
      console.log('Is valid:', response.data.isValid);
      console.log('Message:', response.data.message);
      
      setVerificationResult({
        isValid: response.data.isValid,
        message: response.data.message,
        productCode: response.data.productCode,
        regionName: response.data.regionName,
        verificationTime: response.data.verificationTime,
        location: response.data.location,
        // Show previous verification time if already verified
        previousVerificationTime: response.data.verificationTime && !response.data.isValid ? response.data.verificationTime : null,
        previousLocation: response.data.location && !response.data.isValid ? response.data.location : null
      });

      // Clear the authenticity code field if verification is successful
      if (response.data.isValid) {
        setAuthenticityCode('');
      }
      
    } catch (err) {
      console.error('Verification failed:', err);
      setError('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet key={location.pathname}>
        <title>Check Authenticity – Verify Genuine India Tech Speakers</title>
        <meta 
          name="description" 
          content="Ensure your Saturn Multimedia Tower Speakers are 100 percent genuine. Enter your serial number to confirm product authenticity and protect your audio experience with India Tech." 
        />
        <meta property="og:title" content="Check Authenticity – Verify Genuine India Tech Speakers" />
        <meta property="og:description" content="Ensure your Saturn Multimedia Tower Speakers are 100 percent genuine. Enter your serial number to confirm product authenticity and protect your audio experience with India Tech." />
        <meta property="twitter:title" content="Check Authenticity – Verify Genuine India Tech Speakers" />
        <meta property="twitter:description" content="Ensure your Saturn Multimedia Tower Speakers are 100 percent genuine. Enter your serial number to confirm product authenticity and protect your audio experience with India Tech." />
      </Helmet>
      <Container maxWidth="md" sx={{ py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: 'stretch',
        }}
      >
        {/* Left Section - Check Authenticity */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            flex: { xs: '0 0 auto', md: '1 1 50%' },
            order: { xs: 2, md: 1 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#000',
                mb: 1,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Check Authenticity
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#000',
                mb: 4,
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Check. Confirm. Enjoy Genuine Quality.
            </Typography>

            {/* QR Scan Requirement */}
            {/* {!qrCodeScanned && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  QR Code Scan Required
                </Typography>
                <Typography variant="body2">
                  Please scan the QR code on your product first to verify the region.
                </Typography>
              </Alert>
            )} */}

            {/* Region Verification Status */}
            {region && qrCodeScanned && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  Region Verified: {region.name}
                </Typography>
              </Alert>
            )}

            {/* Verification Form */}
            {/* <Paper
              elevation={3}
              sx={{
                p: 4,
                borderRadius: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
              }}
            > */}
              <Box component="form" onSubmit={handleVerifyProduct} sx={{ flex: 1,  }}>
                {/* Unique Code Field */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: '#000',
                      mb: 1
                    }}
                  >
                    Enter Unique code
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      fullWidth
                      placeholder="e.g. #H876543"
                      value={authenticityCode}
                      onChange={(e) => setAuthenticityCode(e.target.value.toUpperCase())}
                      // disabled={loading || !qrCodeScanned}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: '1.1rem',
                        }
                      }}
                    />
                    {/* <Button
                  variant="contained"
                  sx={{ 
                    backgroundColor: "#c1ec4b",
                    color: "black",
                    px: 4,
                    py: 2,
                    borderRadius: 2,
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    "&:hover": { 
                      backgroundColor: "#45a049" 
                    }
                  }}
                  endIcon={<Box sx={{ ml: 1 }}>→</Box>}
                >
                  Shop Now
                </Button> */}
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        backgroundColor: "#c1ec4b",
                        borderRadius: 2,
                        // background: 'linear-gradient(45deg, #2596be 0%, #4fb3d9 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <VerifiedUserIcon sx={{ color: '#000', fontSize: 30 }} />
                    </Box>
                  </Box>
                </Box>

                {/* Mobile Field */}
                {/* <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#000',
                      mb: 1
                    }}
                  >
                    Mobile
                  </Typography>
          <TextField
            fullWidth
                    placeholder="Mobile Number"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                      }
                    }}
                  />
                </Box> */}

                {/* Email Field */}
                {/* <Box sx={{ mb: 4 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: '#000',
                      mb: 1
                    }}
                  >
                    Email (optional)
                  </Typography>
                  <TextField
              fullWidth
                    placeholder="Email Id (Optional)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '1.1rem',
                      }
                    }}
                  />
                </Box> */}

                {/* Check Now Button */}
                <Button
                  type="submit"
                  variant="contained"
                  // disabled={loading || !qrCodeScanned}
                  sx={{
                    width: '100%',
                    height: 60,
                    mb: 2,
                    backgroundColor: "#c1ec4b",
                    color: "black",
                    px: 4,
                    py: 2,
                    borderRadius: 2,
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    '&:hover': {
                      backgroundColor: '#abda29',
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#666',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Check Now'}
                </Button>

                {/* Subscribe Checkbox */}
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={subscribe}
                      onChange={(e) => setSubscribe(e.target.checked)}
                      sx={{
                        color: '#2596be',
                        '&.Mui-checked': {
                          color: '#2596be',
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#000' }}>
                      Subscribe me to articles, deals and more!
                    </Typography>
                  }
                /> */}
              </Box>
            {/* </Paper> */}
          </Box>
        </Box>

        {/* Right Section - Product Image */}
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            flex: { xs: '0 0 auto', md: '1 1 50%' },
            order: { xs: 1, md: 2 },
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, md: 4 },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: { xs: '300px', md: '400px' },
              backgroundColor: 'background.paper',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={mainImage}
              alt="Speaker Tower"
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '400px', md: '500px' },
                height: 'auto',
                maxHeight: { xs: '350px', md: '550px' },
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
              }}
            />
          </Paper>
        </Box>
      </Box>

      {/* Verification Result Modal */}
      {verificationResult && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Paper
            sx={{
              p: 2,
              maxWidth: 800,
              width: '80%',
              borderRadius: 2,
              display: 'flex',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
            }}
          >
            {/* Left Side - All Text Content */}
            <Box sx={{ flex: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {verificationResult.isValid ? (
                  <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mr: 2 }} />
                ) : (
                  <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mr: 2 }} />
                )}
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: verificationResult.isValid ? 'success.main' : 'error.main'
                  }}
                >
                  {verificationResult.isValid ? 'Verification Successful!' : 'Verification Failed'}
                </Typography>
              </Box>

              <Typography variant="h6" sx={{ mb: 3, textAlign: 'left' }}>
                {verificationResult.message}
              </Typography>
              
              {verificationResult.isValid && (
                <>
                  <Typography variant="body1" sx={{ mb: 3, fontWeight: 'medium', color: 'success.main', textAlign: 'left' }}>
                    Authenticity Confirmed – You Own a Genuine India Tech Product
                  </Typography>
                  <Box sx={{ mb: 3, textAlign: 'left' }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                      <strong>Verified At:</strong> {verificationResult.verificationTime}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Location:</strong> {verificationResult.location}
                    </Typography>
                  </Box>
                </>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => setVerificationResult(null)}
                  sx={{
                    backgroundColor: verificationResult.isValid ? '#4caf50' : '#f44336',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: verificationResult.isValid ? '#45a049' : '#da190b',
                    }
                  }}
                >
                  {verificationResult.isValid ? 'Continue' : 'Try Again'}
                </Button>
              </Box>
            </Box>

            {/* Right Side - Image */}
            {verificationResult.isValid && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'end',
                  alignItems: 'center',
                }}
              >
                <Box
                  component="img"
                  src={TowerImage}
                  alt="Product"
                  sx={{
                    maxWidth: '100%',
                    width: 'auto',
                    height: 'auto',
                    maxHeight: '350px',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Error Messages */}
        {error && (
        <Alert 
          severity="error" 
          onClick={() => setError('')}
          sx={{ 
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 10000,
            minWidth: 300,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            }
          }}
        >
            {error}
          </Alert>
        )}

      {success && (
          <Alert
          severity="success" 
          onClick={() => setSuccess('')}
          sx={{ 
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 10000,
            minWidth: 300,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            }
          }}
        >
          {success}
          </Alert>
        )}
    </Container>
    </>
  );
};

export default ProductVerificationPage;